import { z } from 'zod';
import { CANONICAL_CATEGORY_SLUGS, EVENT_LIFECYCLE_STATUSES } from '@loci/types';

/**
 * Custom boolean coercion that handles string 'false' correctly
 *
 * @description Zod's built-in boolean coercion converts any non-empty string to true,
 * including the string 'false'. This custom schema properly handles string boolean values.
 *
 * @example
 * ```typescript
 * booleanCoerce.parse(true) // true
 * booleanCoerce.parse('true') // true
 * booleanCoerce.parse('false') // false
 * booleanCoerce.parse(1) // true
 * booleanCoerce.parse(0) // false
 * ```
 */
export const booleanCoerce = z
  .union([
    z.boolean(),
    z.string().transform((val) => {
      if (val === 'true') return true;
      if (val === 'false') return false;
      return Boolean(val);
    }),
    z.number().transform(Boolean),
  ])
  .optional();

/**
 * Strict boolean coercion for query params — only accepts 'true'/'false' strings
 * and boolean values. Rejects unexpected values like '0', 'no', or arbitrary strings.
 *
 * Exported so `events.ts` (and other query-schema modules) reuse one canonical
 * boolean-query coercion instead of duplicating it.
 */
export const strictBooleanQuery = z.preprocess((val) => {
  if (val === undefined || val === null || val === '') return undefined;
  if (typeof val === 'boolean') return val;
  if (val === 'true') return true;
  if (val === 'false') return false;
  return val; // let z.boolean() reject it
}, z.boolean().optional());

// Base validation schemas

/**
 * Geographic coordinates validation schema
 *
 * @description Validates latitude and longitude values according to WGS84/EPSG:4326 standards.
 * Coordinate System: WGS84 (EPSG:4326)
 * Latitude must be between -90 and 90 degrees.
 * Longitude must be between -180 and 180 degrees.
 * Order: { latitude, longitude } or [longitude, latitude] (GeoJSON format)
 *
 * @example
 * ```typescript
 * coordinatesSchema.parse({ latitude: 40.7128, longitude: -74.0060 }) // NYC coordinates
 * ```
 */
export const coordinatesSchema = z.object({
  latitude: z
    .number()
    .min(-90, 'Latitude must be >= -90 (WGS84/EPSG:4326)')
    .max(90, 'Latitude must be <= 90 (WGS84/EPSG:4326)')
    .refine((val) => Number.isFinite(val), 'Latitude must be a finite number'),
  longitude: z
    .number()
    .min(-180, 'Longitude must be >= -180 (WGS84/EPSG:4326)')
    .max(180, 'Longitude must be <= 180 (WGS84/EPSG:4326)')
    .refine((val) => Number.isFinite(val), 'Longitude must be a finite number'),
});

/**
 * GeoJSON coordinate pair validation schema
 *
 * @description Validates a coordinate pair in GeoJSON format [longitude, latitude]
 * Note: GeoJSON uses [lng, lat] order, NOT [lat, lng]
 * Coordinate System: WGS84 (EPSG:4326)
 *
 * @example
 * ```typescript
 * geoJSONCoordinateSchema.parse([-74.0060, 40.7128]) // NYC coordinates in GeoJSON format
 * ```
 */
export const geoJSONCoordinateSchema = z
  .tuple([
    z
      .number()
      .min(-180, 'Longitude must be >= -180 (WGS84/EPSG:4326)')
      .max(180, 'Longitude must be <= 180 (WGS84/EPSG:4326)'),
    z
      .number()
      .min(-90, 'Latitude must be >= -90 (WGS84/EPSG:4326)')
      .max(90, 'Latitude must be <= 90 (WGS84/EPSG:4326)'),
  ])
  .refine(
    ([lng, lat]) => Number.isFinite(lng) && Number.isFinite(lat),
    'Coordinates must be finite numbers',
  );

/**
 * Date string validation schema
 *
 * @description Validates that a string can be parsed as a valid date.
 * Accepts any format that JavaScript's Date.parse() can handle.
 *
 * @example
 * ```typescript
 * dateSchema.parse('2024-01-15') // Valid ISO date
 * dateSchema.parse('January 15, 2024') // Valid US format
 * dateSchema.parse('15/01/2024') // Valid European format
 * ```
 */
export const dateSchema = z.string().refine((date: string) => !isNaN(Date.parse(date)), {
  message: 'Invalid date format',
});

/**
 * Optional URL validation schema
 *
 * @description Validates URL format when provided. The field is optional.
 *
 * @example
 * ```typescript
 * urlSchema.parse('https://example.com') // Valid
 * urlSchema.parse(undefined) // Valid (optional)
 * urlSchema.parse('not-a-url') // Invalid
 * ```
 */
export const urlSchema = z.string().url().optional();

/**
 * Price format validation schema
 *
 * @description Validates price strings in the format $XX.XX or $XX.
 * The field is optional.
 *
 * @example
 * ```typescript
 * priceSchema.parse('$25.00') // Valid
 * priceSchema.parse('$25') // Invalid (missing .00)
 * priceSchema.parse(undefined) // Valid (optional)
 * ```
 */

export const priceSchema = z
  .string()
  .regex(/^\$\d+(\.\d{2})?$/, {
    message: 'Price must be in format $XX.XX',
  })
  .optional();

// Event category enum — the closed ten (COR-37), taken from `@loci/types`
// rather than restated (API-1719).
export const eventCategorySchema = z.enum(CANONICAL_CATEGORY_SLUGS);

// Venue schema
export const venueSchema = z.object({
  id: z.string().optional(),
  name: z.string().min(1, 'Venue name is required'),
  address: z.string().min(1, 'Venue address is required'),
  city: z.string().min(1, 'City is required'),
  state: z.string().min(2).max(2, 'State must be 2 characters').optional(),
  country: z.string().min(2).max(3, 'Country code required').default('US'),
  coordinates: coordinatesSchema,
  capacity: z.number().positive().optional(),
  website: urlSchema,
  phone: z.string().optional(),
});

// Artist/Performer schema
export const artistSchema = z.object({
  id: z.string().optional(),
  name: z.string().min(1, 'Artist name is required'),
  genre: z.string().optional(),
  website: urlSchema,
  social: z
    .object({
      instagram: z.string().optional(),
      twitter: z.string().optional(),
      facebook: z.string().optional(),
      spotify: z.string().optional(),
    })
    .optional(),
});

// Main event schema
export const eventSchema = z.object({
  id: z.string().min(1, 'Event ID is required'),
  title: z.string().min(1, 'Event title is required'),
  description: z.string().min(10, 'Description must be at least 10 characters'),

  // Date and time
  date: dateSchema,
  startTime: z.string().optional(),
  endTime: z.string().optional(),
  timezone: z.string().default('America/New_York'),

  // Categorization
  category: eventCategorySchema,
  tags: z.array(z.string()).default([]),

  // Location
  venue: venueSchema,
  location: z.string().min(1, 'Location description required'),

  // Legacy coordinates (for backward compatibility)
  latitude: z.number().min(-90).max(90),
  longitude: z.number().min(-180).max(180),

  // Performers
  artists: z.array(artistSchema).default([]),

  // Ticketing
  ticketUrl: urlSchema,
  price: priceSchema,
  availableTickets: z.number().nonnegative().optional(),
  soldOut: z.boolean().default(false),

  // Metadata
  source: z.string().min(1, 'Data source is required'),
  sourceId: z.string().optional(),
  confidence: z.number().min(0).max(1).default(0.5),
  verified: z.boolean().default(false),

  // Timestamps
  createdAt: z.string().datetime().optional(),
  updatedAt: z.string().datetime().optional(),

  // Additional info
  ageRestriction: z.string().optional(),
  accessibility: z.string().optional(),
  images: z.array(z.string().url()).default([]),
});

// Event update schema (partial)
export const eventUpdateSchema = eventSchema.partial().extend({
  id: z.string().min(1, 'Event ID is required'),
});

// Event search/filter schema
export const eventSearchSchema = z.object({
  query: z.string().optional(),
  category: eventCategorySchema.optional(),
  location: z.string().optional(),
  coordinates: coordinatesSchema.optional(),
  radius: z.number().positive().default(25),
  dateFrom: dateSchema.optional(),
  dateTo: dateSchema.optional(),
  priceMin: z.number().nonnegative().optional(),
  priceMax: z.number().positive().optional(),
  verified: z.boolean().optional(),
  limit: z.number().positive().max(100).default(20),
  offset: z.number().nonnegative().default(0),
});

// Data quality metrics schema
export const dataQualityMetricsSchema = z.object({
  totalEvents: z.number().nonnegative(),
  validEvents: z.number().nonnegative(),
  invalidEvents: z.number().nonnegative(),
  duplicatesFound: z.number().nonnegative(),
  duplicatesRemoved: z.number().nonnegative(),
  averageConfidence: z.number().min(0).max(1),
  sourcesCount: z.number().nonnegative(),
  lastProcessed: z.string().datetime(),
});

// Duplicate detection result schema
export const duplicateDetectionResultSchema = z.object({
  isDuplicate: z.boolean(),
  confidence: z.number().min(0).max(1),
  matchedEvent: eventSchema.optional(),
  reasons: z.array(z.string()).default([]),
  action: z.enum(['merge', 'replace', 'ignore', 'manual_review']),
});

// Manual review queue item schema
export const manualReviewItemSchema = z.object({
  id: z.string(),
  originalEvent: eventSchema,
  potentialDuplicate: eventSchema,
  confidence: z.number().min(0).max(1),
  reasons: z.array(z.string()),
  status: z.enum(['pending', 'approved', 'rejected', 'merged']).default('pending'),
  reviewedBy: z.string().optional(),
  reviewedAt: z.string().datetime().optional(),
  createdAt: z.string().datetime(),
});

// Event creation schema for API endpoints
export const createEventSchema = z.object({
  title: z.string().min(1, 'Event title is required'),
  description: z.string().min(10, 'Description must be at least 10 characters').optional(),
  date: z.coerce.date(),
  endDate: z.coerce.date().optional(),
  imageUrl: urlSchema,
  ticketUrl: urlSchema,
  price: z.number().positive().optional(),
  currency: z.string().length(3).default('USD'),
  ageRestriction: z.string().optional(),
  accessibility: z.string().optional(),
  tags: z.array(z.string()).default([]),
  // Derived from the single source of truth in @loci/types (API-1618) — covers
  // every Prisma EventStatus member (incl. SOLD_OUT / POSTPONED / OFFSALE) so
  // the write path no longer rejects valid statuses. Parity with Prisma is
  // pinned at compile time in backend-api's browseEventStatus.ts.
  status: z.enum(EVENT_LIFECYCLE_STATUSES).default('ACTIVE'),
  timezone: z.string().default('America/New_York'),
  sourceId: z.string().optional(),
  sourceEventId: z.string().optional(),
  sourceUrl: urlSchema,
  locationId: z.string().cuid().optional(),
  coordinates: coordinatesSchema.optional(),
});

// Bounding box validation schema - expects minLon,minLat,maxLon,maxLat
const bboxSchema = z
  .string()
  .regex(
    /^-?\d+\.?\d*,-?\d+\.?\d*,-?\d+\.?\d*,-?\d+\.?\d*$/,
    'Bbox must be in format: minLon,minLat,maxLon,maxLat',
  )
  .transform((bbox) => {
    const [minLon, minLat, maxLon, maxLat] = bbox.split(',').map(Number);
    return { minLon, minLat, maxLon, maxLat };
  })
  .refine(
    ({ minLon, minLat, maxLon, maxLat }) =>
      minLon >= -180 && maxLon <= 180 && minLat >= -90 && maxLat <= 90,
    'Bbox coordinates must be within valid ranges: longitude [-180,180], latitude [-90,90]',
  )
  .refine(
    ({ minLon, minLat, maxLon, maxLat }) => minLon < maxLon && minLat < maxLat,
    'Bbox minimum coordinates must be less than maximum coordinates',
  )
  .refine(({ minLon, minLat, maxLon, maxLat }) => {
    // Check if bbox area is reasonable (not too large)
    const lonDiff = maxLon - minLon;
    const latDiff = maxLat - minLat;
    return lonDiff <= 10 && latDiff <= 10; // Max ~1000km x 1000km area
  }, 'Bounding box area is too large. Maximum size is 10 degrees longitude by 10 degrees latitude');

// Helper for comma-separated strings or arrays
const commaSeparatedToArray = z
  .union([z.array(z.string()), z.string()])
  .transform((val) => {
    if (Array.isArray(val)) return val;
    return val
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
  })
  .optional();

// Base schema for spatial search (without refinement so it can be extended)
const nearbyEventsBaseSchema = z.object({
  // Radius-based search parameters (optional when using bbox)
  lat: z.coerce.number().min(-90).max(90, 'Latitude must be between -90 and 90').optional(),
  lon: z.coerce.number().min(-180).max(180, 'Longitude must be between -180 and 180').optional(),
  radius: z.coerce.number().positive().max(100, 'Radius must be between 1 and 100 km').default(25),

  // Bounding box search parameter (alternative to lat/lon/radius)
  bbox: bboxSchema.optional(),

  // Common parameters for both search types
  limit: z.coerce.number().positive().max(100, 'Limit must be between 1 and 100').default(50),

  // Pagination parameters (cursor-based preferred, offset for backward compatibility)
  cursor: z.string().optional(), // Base64-encoded cursor for efficient pagination
  offset: z.coerce.number().nonnegative().default(0), // DEPRECATED: Use cursor instead

  categories: commaSeparatedToArray,
  dateFrom: z.coerce.date().optional(),
  dateTo: z.coerce.date().optional(),
});

// Refinement function to ensure either bbox OR lat/lon is provided
const spatialQueryRefinement = (data: {
  lat?: number;
  lon?: number;
  bbox?: { minLon: number; minLat: number; maxLon: number; maxLat: number };
  radius: number;
  limit: number;
  offset: number;
  categories?: string[];
  dateFrom?: Date;
  dateTo?: Date;
}) => {
  // Either bbox OR (lat AND lon) must be provided, not both
  const hasBbox = !!data.bbox;
  const hasLatLon = data.lat !== undefined && data.lon !== undefined;
  return hasBbox !== hasLatLon; // XOR: exactly one must be true
};

// API query schemas for spatial search
export const nearbyEventsQuerySchema = nearbyEventsBaseSchema.refine(spatialQueryRefinement, {
  message: 'Either provide "bbox" OR both "lat" and "lon" parameters, not both',
  path: ['bbox'], // Show error on bbox field
});

// Reject blank query strings before numeric coercion (z.coerce.number() turns '' into 0)
const nonBlankQueryNumber = (schema: z.ZodNumber) =>
  z.preprocess(
    (value) => (typeof value === 'string' && value.trim() === '' ? undefined : value),
    schema,
  );

// Shared coordinate bounds for query params (WGS84)
const latQuerySchema = nonBlankQueryNumber(
  z.coerce
    .number()
    .min(-90, 'Latitude must be between -90 and 90')
    .max(90, 'Latitude must be between -90 and 90'),
);
const lonQuerySchema = nonBlankQueryNumber(
  z.coerce
    .number()
    .min(-180, 'Longitude must be between -180 and 180')
    .max(180, 'Longitude must be between -180 and 180'),
);
const radiusKmSchema = z.coerce
  .number()
  .positive('Radius must be positive')
  .max(100, 'Radius must be at most 100 km')
  .default(25);

/**
 * Query schema for GET /events/popular-areas
 */
export const popularAreasQuerySchema = z.object({
  lat: latQuerySchema,
  lon: lonQuerySchema,
  radius: radiusKmSchema,
  timeframeDays: z.coerce
    .number()
    .int('Timeframe must be an integer')
    .positive('Timeframe must be positive')
    .max(365, 'Timeframe must be at most 365 days')
    .default(30),
});

/** Single landmark name: non-empty after trim. */
const landmarkNameSchema = z.string().trim().min(1, 'Landmark name is required');

/**
 * Normalize landmarks to string[] (single string or array of strings from query).
 * Rejects whitespace-only strings and empty arrays.
 */
const landmarksArraySchema = z
  .union([
    z.array(landmarkNameSchema).min(1, 'At least one landmark is required'),
    landmarkNameSchema,
  ])
  .transform((val) => (Array.isArray(val) ? val : [val]));

/**
 * Query schema for GET /events/landmarks
 */
export const landmarksQuerySchema = z.object({
  lat: latQuerySchema,
  lon: lonQuerySchema,
  landmarks: landmarksArraySchema,
  radius: radiusKmSchema,
});

/**
 * Query schema for GET /location/reverse (reverse geocode)
 */
export const reverseGeocodeQuerySchema = z.object({
  lat: latQuerySchema,
  lon: lonQuerySchema,
});

/**
 * Shared date string validation for both date-only (YYYY-MM-DD) and ISO 8601 datetime formats
 */
const dateStringValidator = (val: string) => {
  // Check if it's a valid date-only format (YYYY-MM-DD)
  if (/^\d{4}-\d{2}-\d{2}$/.test(val)) {
    return !isNaN(Date.parse(val));
  }
  // Check if it's a valid ISO 8601 datetime
  return !isNaN(Date.parse(val));
};

const dateStringSchema = z
  .string()
  .refine(dateStringValidator, {
    message: 'Must be a valid date (YYYY-MM-DD) or ISO 8601 datetime',
  })
  .optional();

/**
 * Date filtering schema with timezone support
 *
 * Supports:
 * - Single date filtering (events on specific date)
 * - Date range filtering (events between dates)
 * - Timezone-aware queries with IANA timezone identifiers
 * - Both date-only (YYYY-MM-DD) and datetime (ISO 8601) formats
 *
 * @example
 * ```typescript
 * // Single date
 * { startDate: '2025-01-15', timezone: 'America/New_York' }
 *
 * // Date range
 * { startDate: '2025-01-15', endDate: '2025-01-20', timezone: 'America/Los_Angeles' }
 *
 * // Datetime format
 * { startDate: '2025-01-15T14:00:00Z', endDate: '2025-01-15T18:00:00Z' }
 * ```
 */
export const dateFilterSchema = z
  .object({
    startDate: dateStringSchema,
    endDate: dateStringSchema,
    timezone: z.string().optional().default('UTC'),
  })
  .refine(
    (data) => {
      if (!data.endDate || !data.startDate) return true;
      // Parse dates for proper comparison
      const start = new Date(data.startDate);
      const end = new Date(data.endDate);
      return end >= start;
    },
    { message: 'endDate must be after or equal to startDate' },
  );

export const searchSortSchema = z.enum(['relevance', 'date', 'distance', 'price']);
export type SearchSort = z.infer<typeof searchSortSchema>;

export const eventSearchQuerySchema = z
  .object({
    q: z.string().optional(),
    categories: commaSeparatedToArray,
    /** Single category slug or comma-separated list (alias for `categories`, legacy parity with unified search). */
    category: z
      .union([z.string(), z.array(z.string())])
      .optional()
      .transform((val) => {
        if (val === undefined) return undefined;
        const parts = (Array.isArray(val) ? val : [val])
          .flatMap((s) => s.split(','))
          .map((s) => s.trim())
          .filter(Boolean);
        return parts.length ? parts : undefined;
      }),
    // Generic free-form location string (backwards compatible)
    location: z.string().optional(),
    // Structured location filters (preferred for new callers)
    city: z.string().optional(),
    state: z.string().optional(),
    // Spatial filtering (PostGIS ST_DWithin)
    lat: latQuerySchema.optional(),
    lon: lonQuerySchema.optional(),
    radius: z.coerce.number().positive().max(100).optional(), // miles
    // Date filtering (legacy support with coercion)
    dateFrom: z.coerce.date().optional(),
    dateTo: z.coerce.date().optional(),
    // Enhanced date filtering with timezone support
    startDate: dateStringSchema,
    endDate: dateStringSchema,
    timezone: z.string().optional().default('UTC'),
    // Price filtering (prices in cents as integers)
    minPrice: z.coerce.number().int().nonnegative().optional(),
    maxPrice: z.coerce.number().int().nonnegative().optional(),
    currency: z.string().length(3).optional().default('USD'),
    includeFree: booleanCoerce.default(true),
    // Venue filters. COR-62 removed the indoor/outdoor `venueType` filter
    // along with `Location.isOutdoor`, its only backing column.
    wheelchair: strictBooleanQuery,
    parking: strictBooleanQuery,
    transit: strictBooleanQuery,
    // Quality band floor (API-1511) — deep `show_all` opt-in; re-includes
    // LOW_CONFIDENCE rows once the quality gate is enabled.
    showAll: strictBooleanQuery,
    // Sort and pagination
    sort: searchSortSchema.optional().default('date'),
    page: z.coerce.number().positive().default(1),
    limit: z.coerce.number().positive().max(100).default(20),
    offset: z.coerce.number().nonnegative().optional(),
  })
  .refine(
    (data) => {
      // Validate endDate is after startDate if both provided
      if (data.startDate && data.endDate) {
        const start = new Date(data.startDate);
        const end = new Date(data.endDate);
        if (end < start) return false;
      }
      // Validate dateTo is after dateFrom if both provided (legacy)
      if (data.dateFrom && data.dateTo) {
        return data.dateTo >= data.dateFrom;
      }
      return true;
    },
    { message: 'End date must be after or equal to start date' },
  )
  .refine(
    (data) => {
      // Validate maxPrice >= minPrice if both provided
      if (data.minPrice !== undefined && data.maxPrice !== undefined) {
        return data.maxPrice >= data.minPrice;
      }
      return true;
    },
    { message: 'maxPrice must be >= minPrice' },
  )
  .refine(
    (data) => {
      // If lat or lon is provided, both must be present
      if ((data.lat !== undefined) !== (data.lon !== undefined)) {
        return false;
      }
      return true;
    },
    { message: 'Both lat and lon must be provided together' },
  )
  .refine(
    (data) => {
      // sort=distance requires lat/lon
      if (data.sort === 'distance' && (data.lat === undefined || data.lon === undefined)) {
        return false;
      }
      return true;
    },
    { message: 'sort=distance requires lat and lon parameters' },
  )
  .transform((data) => {
    const { category, categories, ...rest } = data;
    const merged = [...(categories ?? []), ...(category ?? [])]
      .map((s) => s.trim())
      .filter(Boolean);
    const unique = merged.length ? [...new Set(merged)] : undefined;
    return { ...rest, categories: unique };
  });

/** Sort options for GET /events/search (API-1182); excludes `price` from legacy `searchSortSchema`. */
export const unifiedSearchSortBySchema = z.enum(['relevance', 'distance', 'date']);
export type UnifiedSearchSortBy = z.infer<typeof unifiedSearchSortBySchema>;

/**
 * Unified GET /events/search query (API-1182).
 * Accepts `category` (comma-separated or repeated) as an alias for `categories`.
 * Spatial params are optional unless `sortBy` is `distance`.
 */
export const unifiedSearchQuerySchema = z
  .object({
    q: z.string().optional(),
    lat: latQuerySchema.optional(),
    lon: lonQuerySchema.optional(),
    /** Search radius in kilometers (same unit as nearby endpoints). */
    radius: z.coerce.number().positive().max(100).optional().default(25),
    bbox: bboxSchema.optional(),
    categories: commaSeparatedToArray,
    /** Single category slug or comma-separated list (alias for categories). */
    category: z
      .union([z.string(), z.array(z.string())])
      .optional()
      .transform((val) => {
        if (val === undefined) return undefined;
        const parts = (Array.isArray(val) ? val : [val])
          .flatMap((s) => s.split(','))
          .map((s) => s.trim())
          .filter(Boolean);
        return parts.length ? parts : undefined;
      }),
    dateFrom: z.coerce.date().optional(),
    dateTo: z.coerce.date().optional(),
    startDate: dateStringSchema,
    endDate: dateStringSchema,
    timezone: z.string().optional().default('UTC'),
    sortBy: unifiedSearchSortBySchema.optional().default('date'),
    limit: z.coerce.number().positive().max(100).default(20),
    offset: z.coerce.number().nonnegative().default(0),
    page: z.coerce.number().positive().optional(),
    /**
     * Deep / `show_all` opt-in (API-1511). When `true`, the `LOW_CONFIDENCE`
     * band floor is lifted so low-confidence events resurface in the deep
     * results. Omitted / `false` keeps them out of the default search body once
     * the quality gate (`LOCI_QUALITY_GATE_ENABLED`) is enabled; no effect
     * while the gate is OFF.
     */
    showAll: strictBooleanQuery,
  })
  .refine(
    (data) => {
      if ((data.lat !== undefined) !== (data.lon !== undefined)) return false;
      return true;
    },
    { message: 'Both lat and lon must be provided together', path: ['lon'] },
  )
  .refine(
    (data) => {
      // lat/lon/radius and bbox are mutually exclusive
      const hasCenter = data.lat !== undefined && data.lon !== undefined;
      if (hasCenter && data.bbox !== undefined) return false;
      return true;
    },
    { message: 'Cannot provide both lat/lon and bbox', path: ['bbox'] },
  )
  .refine(
    (data) => {
      if (data.sortBy === 'distance' && (data.lat === undefined || data.lon === undefined)) {
        return false;
      }
      return true;
    },
    { message: 'sortBy=distance requires lat, lon, and radius', path: ['sortBy'] },
  )
  .refine(
    (data) => {
      if (data.dateFrom && data.dateTo && data.dateTo < data.dateFrom) return false;
      return true;
    },
    { message: 'dateTo must be after or equal to dateFrom', path: ['dateTo'] },
  )
  .refine(
    (data) => {
      if (data.startDate && data.endDate) {
        const start = new Date(data.startDate);
        const end = new Date(data.endDate);
        if (end < start) return false;
      }
      return true;
    },
    { message: 'endDate must be after or equal to startDate', path: ['endDate'] },
  )
  .transform((data) => {
    const { category, categories, ...rest } = data;
    const merged = [...(categories ?? []), ...(category ?? [])]
      .map((s) => s.trim())
      .filter(Boolean);
    const unique = merged.length ? [...new Set(merged)] : undefined;
    return { ...rest, categories: unique };
  });

export type UnifiedSearchQuery = z.infer<typeof unifiedSearchQuerySchema>;

// Venue type enumeration for validation.
// Must stay byte-for-byte aligned with the Prisma `VenueType` enum in
// `packages/db-core/prisma/schema.prisma` — Zod is the Express-layer
// boundary validator, Prisma is the column constraint, and the two
// drift silently if they diverge. COR-8 added `ARENA` for the Refine
// admin Venues directory; COR-128 added the four OSM `amenity=` members.
export const venueTypeSchema = z.enum([
  'CONCERT_HALL',
  'PARK',
  'STADIUM',
  'THEATER',
  'CLUB',
  'RESTAURANT',
  'BAR',
  'CONFERENCE_CENTER',
  'HOTEL',
  'MUSEUM',
  'GALLERY',
  'OUTDOOR_SPACE',
  'COMMUNITY_CENTER',
  'SCHOOL',
  'UNIVERSITY',
  'SPORTS_FACILITY',
  'SHOPPING_CENTER',
  'ARENA',
  'PLACE_OF_WORSHIP',
  'EVENT_SPACE',
  'LIBRARY',
  'CINEMA',
  'OTHER',
]);

// Geographic polygon schema for custom area selection
export const geoPolygonSchema = z.object({
  type: z.literal('Polygon'),
  coordinates: z.array(z.array(z.array(z.number().array()))), // GeoJSON polygon format
});

// Transit stop information schema
export const transitStopSchema = z.object({
  type: z.enum(['bus', 'train', 'subway', 'light_rail']),
  distanceMeters: z.number().nonnegative(),
  stopName: z.string(),
  lines: z.array(z.string()).optional(), // Bus/train line numbers
});

// Venue metadata schema matching database structure
export const venueMetadataSchema = z.object({
  type: venueTypeSchema.optional(),
  capacity: z.number().positive().optional(),
  hasParking: z.boolean().optional(),
  transitStops: z.array(transitStopSchema).optional(),
  accessibility: z
    .object({
      wheelchairAccessible: z.boolean().optional(),
    })
    .optional(),
});

// Advanced location filters schema
export const locationFiltersSchema = z.object({
  // Geographic boundaries
  city: z.string().optional(),
  neighborhood: z.string().optional(),
  customPolygon: geoPolygonSchema.optional(),

  // Venue characteristics
  venueType: z.union([venueTypeSchema, z.literal('both')]).optional(),
  capacity: z
    .object({
      min: z.number().nonnegative().optional(),
      max: z.number().positive().optional(),
    })
    .optional(),

  // Accessibility filters
  accessibleByTransit: z.boolean().optional(),
  parkingAvailable: z.boolean().optional(),
  walkingDistanceFromTransit: z.number().nonnegative().optional(), // meters

  // Popular areas and landmarks
  popularAreas: z.boolean().optional(),
  landmarks: z.array(z.string()).optional(),
});

// Advanced search request schema
export const advancedSearchRequestSchema = z.object({
  location: z.object({
    lat: z.number().min(-90).max(90),
    lon: z.number().min(-180).max(180),
  }),
  radius: z.number().positive().max(100).optional().default(25), // km
  filters: locationFiltersSchema.optional(),
  limit: z.number().positive().max(100).optional().default(50),
  offset: z.number().nonnegative().optional().default(0),
  dateFrom: z.coerce.date().optional(),
  dateTo: z.coerce.date().optional(),
  categories: z.array(z.string()).optional(),
});

// Bounding box query schema for rectangular area search
export const bboxEventsQuerySchema = z.object({
  bbox: z
    .string()
    .regex(
      /^-?\d+(\.\d+)?,-?\d+(\.\d+)?,-?\d+(\.\d+)?,-?\d+(\.\d+)?$/,
      'Bounding box must be in format "minLng,minLat,maxLng,maxLat"',
    ),
  limit: z.coerce.number().positive().max(100, 'Limit must be between 1 and 100').default(50),
  offset: z.coerce.number().nonnegative().default(0),
  categories: commaSeparatedToArray,
  dateFrom: z.coerce.date().optional(),
  dateTo: z.coerce.date().optional(),
});

// Categories endpoint response schema
export const eventCategoriesResponseSchema = z.object({
  categories: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      slug: z.string(),
      color: z.string().optional(),
      iconName: z.string().optional(),
      count: z.number().nonnegative(),
      description: z.string().optional(),
    }),
  ),
  totalCategories: z.number().nonnegative(),
  timestamp: z.string().datetime(),
});

// WebSocket subscription schemas
export const eventChangeTypeSchema = z.enum(['CREATED', 'UPDATED', 'DELETED', 'STATUS_CHANGED']);

export const eventSubscriptionSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string().optional(),
  date: z.string().datetime(),
  endDate: z.string().datetime().optional(),
  imageUrl: z.string().url().optional(),
  ticketUrl: z.string().url().optional(),
  price: z.string().optional(),
  currency: z.string().optional(),
  location: z
    .object({
      id: z.string(),
      name: z.string(),
      address: z.string().optional(),
      city: z.string().optional(),
      state: z.string().optional(),
      country: z.string().optional(),
    })
    .optional(),
  categories: z
    .array(
      z.object({
        id: z.string(),
        name: z.string(),
        slug: z.string(),
        color: z.string().optional(),
        iconName: z.string().optional(),
      }),
    )
    .optional(),
  artists: z
    .array(
      z.object({
        id: z.string(),
        name: z.string(),
        genre: z.string().optional(),
        imageUrl: z.string().url().optional(),
      }),
    )
    .optional(),
  coordinates: z
    .object({
      latitude: z.number().min(-90).max(90),
      longitude: z.number().min(-180).max(180),
    })
    .optional(),
  status: z.string(),
  updatedAt: z.string().datetime(),
});

export const eventChangeSchema = z.object({
  type: eventChangeTypeSchema,
  event: eventSubscriptionSchema,
  previousEvent: eventSubscriptionSchema.optional(),
  timestamp: z.string().datetime(),
});

export const areaSubscriptionArgsSchema = z
  .object({
    bbox: z
      .string()
      .regex(
        /^-?\d+(\.\d+)?,-?\d+(\.\d+)?,-?\d+(\.\d+)?,-?\d+(\.\d+)?$/,
        'Bounding box must be in format "minLng,minLat,maxLng,maxLat"',
      )
      .optional(),
    lat: z.number().min(-90).max(90).optional(),
    lon: z.number().min(-180).max(180).optional(),
    radius: z.number().positive().max(100).optional(),
  })
  .refine((data) => data.bbox || (data.lat && data.lon && data.radius), {
    message: 'Either bbox or (lat, lon, radius) must be provided',
  });

// Export types
export type Event = z.infer<typeof eventSchema>;
export type EventUpdate = z.infer<typeof eventUpdateSchema>;
export type CreateEvent = z.infer<typeof createEventSchema>;
export type EventSearch = z.infer<typeof eventSearchSchema>;
export type NearbyEventsQuery = z.infer<typeof nearbyEventsQuerySchema>;
export type BboxEventsQuery = z.infer<typeof bboxEventsQuerySchema>;
export type EventSearchQuery = z.infer<typeof eventSearchQuerySchema>;
export type Venue = z.infer<typeof venueSchema>;
export type Artist = z.infer<typeof artistSchema>;
export type EventCategory = z.infer<typeof eventCategorySchema>;
export type DataQualityMetrics = z.infer<typeof dataQualityMetricsSchema>;
export type DuplicateDetectionResult = z.infer<typeof duplicateDetectionResultSchema>;
export type ManualReviewItem = z.infer<typeof manualReviewItemSchema>;
export type PopularAreasQuery = z.infer<typeof popularAreasQuerySchema>;
export type LandmarksQuery = z.infer<typeof landmarksQuerySchema>;
export type ReverseGeocodeQuery = z.infer<typeof reverseGeocodeQuerySchema>;

// Advanced location filtering types
export type VenueType = z.infer<typeof venueTypeSchema>;
export type GeoPolygon = z.infer<typeof geoPolygonSchema>;
export type TransitStop = z.infer<typeof transitStopSchema>;
export type VenueMetadata = z.infer<typeof venueMetadataSchema>;
export type LocationFilters = z.infer<typeof locationFiltersSchema>;
export type AdvancedSearchRequest = z.infer<typeof advancedSearchRequestSchema>;
export type EventCategoriesResponse = z.infer<typeof eventCategoriesResponseSchema>;

// WebSocket subscription types
export type EventChangeType = z.infer<typeof eventChangeTypeSchema>;
export type EventSubscription = z.infer<typeof eventSubscriptionSchema>;
export type EventChange = z.infer<typeof eventChangeSchema>;
export type AreaSubscriptionArgs = z.infer<typeof areaSubscriptionArgsSchema>;

// Eventbrite API schemas
export const eventbriteEventSchema = z.object({
  id: z.string(),
  name: z.object({
    text: z.string(),
    html: z.string(),
  }),
  description: z
    .object({
      text: z.string().optional(),
      html: z.string().optional(),
    })
    .optional(),
  start: z.object({
    timezone: z.string(),
    local: z.string(),
    utc: z.string(),
  }),
  end: z.object({
    timezone: z.string(),
    local: z.string(),
    utc: z.string(),
  }),
  url: z.string().url(),
  capacity: z.number().optional(),
  capacity_is_custom: z.boolean().optional(),
  status: z.string(),
  currency: z.string(),
  listed: z.boolean(),
  shareable: z.boolean(),
  invite_only: z.boolean(),
  online_event: z.boolean(),
  show_remaining: z.boolean(),
  tx_time_limit: z.number().optional(),
  hide_start_date: z.boolean(),
  hide_end_date: z.boolean(),
  locale: z.string(),
  is_locked: z.boolean(),
  privacy_setting: z.string(),
  is_series: z.boolean(),
  is_series_parent: z.boolean(),
  inventory_type: z.string(),
  is_reserved_seating: z.boolean(),
  show_pick_a_seat: z.boolean(),
  show_seatmap_thumbnail: z.boolean(),
  show_colors_in_seatmap_thumbnail: z.boolean(),
  source: z.string(),
  is_free: z.boolean(),
  version: z.string(),
  summary: z.string().optional(),
  logo_id: z.string().optional(),
  organizer_id: z.string(),
  venue_id: z.string().optional(),
  category_id: z.string().optional(),
  subcategory_id: z.string().optional(),
  format_id: z.string().optional(),
  resource_uri: z.string(),
  is_externally_ticketed: z.boolean(),
  logo: z
    .object({
      crop_mask: z
        .object({
          top_left: z.object({ x: z.number(), y: z.number() }),
          width: z.number(),
          height: z.number(),
        })
        .optional(),
      original: z.object({
        url: z.string().url(),
        width: z.number(),
        height: z.number(),
      }),
      id: z.string(),
      url: z.string().url(),
      aspect_ratio: z.string(),
      edge_color: z.string().optional(),
      edge_color_set: z.boolean(),
    })
    .optional(),
  venue: z
    .object({
      address: z
        .object({
          address_1: z.string().optional(),
          address_2: z.string().optional(),
          city: z.string().optional(),
          region: z.string().optional(),
          postal_code: z.string().optional(),
          country: z.string().optional(),
          latitude: z.string().optional(),
          longitude: z.string().optional(),
          localized_address_display: z.string().optional(),
          localized_area_display: z.string().optional(),
          localized_multi_line_address_display: z.array(z.string()).optional(),
        })
        .optional(),
      resource_uri: z.string(),
      id: z.string(),
      age_restriction: z.string().optional(),
      capacity: z.number().optional(),
      name: z.string().optional(),
      latitude: z.string().optional(),
      longitude: z.string().optional(),
    })
    .optional(),
  ticket_availability: z
    .object({
      has_available_tickets: z.boolean(),
      minimum_ticket_price: z
        .object({
          currency: z.string(),
          value: z.number(),
          major_value: z.string(),
          display: z.string(),
        })
        .optional(),
      maximum_ticket_price: z
        .object({
          currency: z.string(),
          value: z.number(),
          major_value: z.string(),
          display: z.string(),
        })
        .optional(),
    })
    .optional(),
});

export type EventbriteEvent = z.infer<typeof eventbriteEventSchema>;

/**
 * Notification preferences (API-1489, extended by API-1493)
 *
 * Allowlisted, per-user notification toggles persisted inside the
 * `User.preferences` JSON blob under the `notifications` key.
 *
 * The schema is `.strict()`, so any key NOT listed below is rejected on PUT
 * with a Zod validation error (e.g. `marketing`, `sms`).
 *
 * - The three in-app / push toggles are **required** — the persisted object
 *   always carries a complete snapshot of those choices.
 * - The two email toggles (`emailDigest`, `emailSavedUpdates`, added in
 *   API-1493) are **optional** on write. This keeps the change non-breaking:
 *   a client that predates them (sending only the three required keys)
 *   still validates, and the read accessor back-fills the missing email
 *   toggles from `NOTIFICATION_PREFERENCE_DEFAULTS`. Making them required
 *   here would 400 every PUT from a not-yet-updated web deploy, since the
 *   backend and frontend ship independently.
 */
export const notificationPreferencesSchema = z
  .object({
    savedEventReminders: z.boolean(),
    newNearYou: z.boolean(),
    priceDrops: z.boolean(),
    emailDigest: z.boolean().optional(),
    emailSavedUpdates: z.boolean().optional(),
  })
  .strict();

export type NotificationPreferences = z.infer<typeof notificationPreferencesSchema>;

/**
 * A notification-preferences snapshot with every toggle resolved (no optional
 * keys). This is what the read accessor returns — defaults are applied for any
 * toggle the stored blob is missing — so consumers (the email-digest job, the
 * settings UI) never have to handle an absent email toggle.
 */
export type ResolvedNotificationPreferences = Required<NotificationPreferences>;

/**
 * Defaults applied when a user has never set notification preferences,
 * or when individual keys are missing from the stored blob. All channels
 * default to opt-in (`true`). The email toggles are gated behind the digest
 * cron being enabled (off by default), so a default of `true` does not send
 * mail until an operator turns the job on.
 */
export const NOTIFICATION_PREFERENCE_DEFAULTS: ResolvedNotificationPreferences = {
  savedEventReminders: true,
  newNearYou: true,
  priceDrops: true,
  emailDigest: true,
  emailSavedUpdates: true,
};

// Authentication schemas are now in auth.ts to avoid duplication
